import { CSSProperties, ReactNode } from "react";
import { Select as AntdSelect } from "antd";
import type { SelectProps as AntdSelectProps } from "antd";
import "antd/dist/reset.css";

interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

interface SelectProps extends Omit<AntdSelectProps, "options" | "children"> {
  options?: SelectOption[];
  placeholder?: string;
  className?: string;
  id?: string;
  style?: CSSProperties;
  ariaLabelledBy?: string;
  ariaDescribedBy?: string;
  children?: ReactNode;
}

export const Select = (props: SelectProps) => {
  const {
    options,
    placeholder,
    className,
    id,
    style,
    ariaLabelledBy,
    ariaDescribedBy,
    children,
    onChange,
    onBlur,
    onFocus,
    ...rest
  } = props;

  return (
    <AntdSelect
      showSearch={false}
      allowClear={!!placeholder}
      placeholder={placeholder || undefined}
      className={className}
      id={id}
      style={style}
      aria-labelledby={ariaLabelledBy}
      aria-describedby={ariaDescribedBy}
      onChange={onChange}
      onBlur={onBlur}
      onFocus={onFocus}
      {...rest}
    >
      {children
        ? children
        : options?.map((option) => (
          <AntdSelect.Option
            key={option.value}
            value={option.value}
            disabled={option.disabled}
          >
            {option.label}
          </AntdSelect.Option>
        ))}
    </AntdSelect>
  );
};

/**
 * A flexible select component built on top of Ant Design's Select.
 * Supports both the `options` prop for simple usage and custom children for advanced customization.
 * Extends all standard Ant Design Select props and provides additional props for accessibility and styling.
 *
 * Interface for individual select option items.
 * @interface SelectOption
 * @property {string} value - The value attribute of the option
 * @property {string} label - The display text for the option
 * @property {boolean} [disabled] - Whether the option is disabled (optional)
 *
 * Props interface for the Select component.
 * Extends Ant Design's Select props and adds custom props for enhanced functionality and accessibility.
 *
 * @interface SelectProps
 * @extends {Omit<AntdSelectProps, "options" | "children">}
 * @property {SelectOption[]} [options] - Array of options to display in the select (optional if using custom children)
 * @property {string} [placeholder] - Placeholder text shown when no option is selected
 * @property {string} [className] - CSS class names for styling
 * @property {string} [id] - Unique identifier for the select element
 * @property {CSSProperties} [style] - Inline styles for the select element
 * @property {string} [ariaLabelledBy] - ID of the element that labels this select
 * @property {string} [ariaDescribedBy] - ID of the element that describes this select
 * @property {ReactNode} [children] - Custom children (e.g., <AntdSelect.Option> elements) for advanced usage
 *
 * @component
 * @example
 * // Basic usage with options array
 * <Select
 *   options={[
 *     { value: "option1", label: "Option 1" },
 *     { value: "option2", label: "Option 2" },
 *     { value: "option3", label: "Option 3", disabled: true }
 *   ]}
 *   placeholder="Select an option"
 *   className="my-select"
 * />
 *
 * @example
 * // With accessibility props and event handlers
 * <Select
 *   options={[
 *     { value: "red", label: "Red" },
 *     { value: "green", label: "Green" },
 *     { value: "blue", label: "Blue" }
 *   ]}
 *   ariaLabelledBy="color-label"
 *   ariaDescribedBy="color-description"
 *   onChange={(value) => console.log('Selected:', value)}
 *   className="color-select"
 * />
 *
 * @example
 * // With custom children (advanced usage)
 * <Select placeholder="Choose...">
 *   <AntdSelect.Option value="">Choose...</AntdSelect.Option>
 *   <AntdSelect.Option value="1">First Option</AntdSelect.Option>
 *   <AntdSelect.Option value="2" disabled>Second Option (disabled)</AntdSelect.Option>
 * </Select>
 **/
